import { useEffect } from 'react';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import type { ICommonStepsConfiguration } from '@aviv-estimation-funnel/shells/src/app/portals/common/commonStepsConfig';
import { addError, initDatadog } from '@mfe-helpers/datadog';
import AppLayout from '@mfe-presentation/components/AppLayout';
import ErrorFallback from '@mfe-presentation/components/ErrorFallback';
import ToastMessage from '@mfe-presentation/components/ToastMessage';
import { AppProvider } from '@mfe-presentation/contexts';
import { TLogo } from '@mfe-types/logo';
import { ShellProviderProps } from '@shells/app/factory';
import type { IImmowebStepsConfiguration } from '@shells-portals/immoweb/immowebFrontConfig';
import { ErrorBoundary } from 'react-error-boundary';
import { isFrenchPortal } from '@mfe-helpers/portalHelpers';
import { NAME_SPACE, useLoadTranslations } from '@mfe-presentation/hooks/useLoadTranslations';
import { useBrowserTitle } from '@mfe-presentation/hooks/useBrowserTitle';

interface IRoot {
    portal: Portal;
    useShellContext?: () => ShellProviderProps | undefined;
    logo: TLogo;
    config: ICommonStepsConfiguration | IImmowebStepsConfiguration;
}

const Root = ({ portal, config, logo, useShellContext }: IRoot) => {
    useLoadTranslations(NAME_SPACE, portal);

    useBrowserTitle(portal);

    useEffect(() => {
        initDatadog(portal);
    }, []);

    useEffect(() => {
        // preload the library to avoid layout shift
        if (!isFrenchPortal(portal)) {
            import(/* webpackChunkName 'lottie-web'*/ 'lottie-web');
        }
    }, []);

    return (
        <ErrorBoundary onError={async (err) => addError(err)} fallback={<ErrorFallback />}>
            <AppProvider
                config={config}
                portal={portal}
                logo={logo}
                useShellContext={useShellContext}
            >
                <ToastMessage />
                <AppLayout logo={logo} />
            </AppProvider>
        </ErrorBoundary>
    );
};

export default Root;
